import React, { useState } from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Stack,
  Chip,
  Alert,
} from '@mui/material';
import { useRootStore } from '../../../store/contexts';
import { graphqlToTime } from '../../utils';

export const BreadcrumbsTab = observer(() => {
  const rootStore = useRootStore();
  const { breadcrumbStack, location, useIndexedDB, persistenceDebug } = rootStore;
  const langStore = rootStore.langStore;
  const currentStore = rootStore.getCurrentPageStore();
  const currentPathname = location?.pathname || '/';

  const [rawData, setRawData] = useState<any>(null);
  const [showRawData, setShowRawData] = useState(false);
  const [showDebugDetails, setShowDebugDetails] = useState(false);

  const handleCopyDebugInfo = async () => {
    const rawDB = await rootStore.persistor.get('breadcrumbs_history');

    const debugInfo = {
      timestamp: new Date().toISOString(),
      currentState: {
        breadcrumbStack,
        currentPathname,
        useIndexedDB,
        currentStore: currentStore ? {
          type: currentStore.constructor.name,
          breadcrumbLabel: (currentStore as any).breadcrumbLabel,
        } : null,
      },
      persistence: {
        lastSave: persistenceDebug.lastSaveTimestamp
          ? new Date(persistenceDebug.lastSaveTimestamp).toISOString()
          : null,
        lastSaveData: persistenceDebug.lastSaveData,
        lastLoad: persistenceDebug.lastLoadTimestamp
          ? new Date(persistenceDebug.lastLoadTimestamp).toISOString()
          : null,
        lastLoadData: persistenceDebug.lastLoadData,
        rawIndexedDB: rawDB,
      },
      eventLog: persistenceDebug.events.map(e => ({
        ...e,
        timestamp: new Date(e.timestamp).toISOString(),
      })),
    };

    navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2));
  };

  const handleClearStack = () => {
    rootStore.clearBreadcrumbs();
  };

  const handleReloadFromDB = async () => {
    await rootStore.loadBreadcrumbs();
  };

  const handleInspectRawDB = async () => {
    const raw = await rootStore.persistor.get('breadcrumbs_history');
    setRawData(raw);
    setShowRawData(true);
  };

  const formatTimestamp = (ts: number | null) => {
    if (!ts) return langStore.monitorNever;
    const date = new Date(ts);
    // 13:45:35.120, sea cual sea el idioma del navegador (en inglés sería 1:45:35 PM).
    return graphqlToTime(date) + '.' + date.getMilliseconds().toString().padStart(3, '0');
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {langStore.monitorBreadcrumbTrail}
      </Typography>

      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {langStore.monitorCurrentTrail}
        </Typography>
        <Typography variant="body1">
          {langStore.monitorInicio}
          {breadcrumbStack.map(crumb => ` > ${crumb.label}`).join('')}
          {currentStore && 'breadcrumbLabel' in currentStore && (currentStore as any).breadcrumbLabel && ` > ${(currentStore as any).breadcrumbLabel}`}
        </Typography>
      </Box>

      <Typography variant="subtitle1" gutterBottom>
        {langStore.monitorStackDetails} ({langStore.monitorItemsCount.replace('{count}', breadcrumbStack.length.toString())})
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{langStore.monitorIndex}</TableCell>
              <TableCell>{langStore.monitorLabel}</TableCell>
              <TableCell>{langStore.monitorPathname}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {breadcrumbStack.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {langStore.monitorStackIsEmptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              breadcrumbStack.map((crumb, index) => (
                <TableRow key={index}>
                  <TableCell>{index}</TableCell>
                  <TableCell>{crumb.label}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {crumb.pathname}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" gutterBottom>
        {langStore.monitorCurrentPageInfo}
      </Typography>
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Stack spacing={1}>
          <Box>
            <Typography variant="caption" color="text.secondary">{langStore.monitorPathname}:</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {currentPathname}
            </Typography>
          </Box>
          {currentStore && (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary">{langStore.monitorStoreType}:</Typography>
                <Typography variant="body2">{currentStore.constructor.name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{langStore.monitorCachedBreadcrumbLabel}:</Typography>
                <Typography variant="body2">{(currentStore as any).breadcrumbLabel || langStore.monitorNotSet}</Typography>
              </Box>
            </>
          )}
          {!currentStore && (
            <Typography variant="body2" color="text.secondary">
              {langStore.monitorNoStoreFoundForCurrentPathname}
            </Typography>
          )}
        </Stack>
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1">
          {langStore.monitorPersistence}
        </Typography>
        <Button
          size="small"
          onClick={() => setShowDebugDetails(!showDebugDetails)}
          variant="text"
        >
          {showDebugDetails ? langStore.monitorHideDebugDetails : langStore.monitorShowDebugDetails}
        </Button>
      </Stack>
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">{langStore.monitorTypeLabel}</Typography>
          <Chip
            label={useIndexedDB ? 'IndexedDB' : 'SessionStorage'}
            size="small"
            color={useIndexedDB ? 'primary' : 'default'}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {useIndexedDB
            ? langStore.monitorUsingIndexedDB
            : langStore.monitorUsingSessionStorage
          }
        </Typography>
      </Box>

      {showDebugDetails && (
        <>
          <Typography variant="subtitle1" gutterBottom>
            {langStore.monitorPersistenceDebugInfo}
          </Typography>
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Stack spacing={1}>
          <Box>
            <Typography variant="caption" color="text.secondary">{langStore.monitorLastSaveDebug}</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
              {formatTimestamp(persistenceDebug.lastSaveTimestamp)}
              {persistenceDebug.lastSaveData && ` (${langStore.monitorItemsCount.replace('{count}', persistenceDebug.lastSaveData.length.toString())})`}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">{langStore.monitorLastLoadDebug}</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
              {formatTimestamp(persistenceDebug.lastLoadTimestamp)}
              {persistenceDebug.lastLoadData && ` (${langStore.monitorItemsCount.replace('{count}', persistenceDebug.lastLoadData.length.toString())})`}
              {persistenceDebug.lastLoadData === null && persistenceDebug.lastLoadTimestamp && ` (${langStore.monitorNull})`}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">{langStore.monitorEventCount}</Typography>
            <Typography variant="body2">{langStore.monitorEventsLogged.replace('{count}', persistenceDebug.events.length.toString())}</Typography>
          </Box>
        </Stack>
      </Box>

      <Typography variant="subtitle1" gutterBottom>
        {langStore.monitorEventLog.replace('{count}', persistenceDebug.events.length.toString())}
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 300 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{langStore.monitorTime}</TableCell>
              <TableCell>{langStore.monitorAction}</TableCell>
              <TableCell>{langStore.monitorData}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {persistenceDebug.events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {langStore.monitorNoEventsLoggedYet}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              persistenceDebug.events.map((event, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {formatTimestamp(event.timestamp)}
                  </TableCell>
                  <TableCell>
                    <Chip label={event.action} size="small" />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {JSON.stringify(event.data)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
        </>
      )}

      {showRawData && (
        <Alert severity="info" onClose={() => setShowRawData(false)} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>{langStore.monitorRawIndexedDBData}</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(rawData, null, 2)}
          </Typography>
        </Alert>
      )}

      <Typography variant="subtitle1" gutterBottom>
        {langStore.monitorActions}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
        <Button variant="contained" size="small" onClick={handleCopyDebugInfo}>
          {langStore.monitorCopyDebugInfo}
        </Button>
        <Button variant="outlined" size="small" color="info" onClick={handleInspectRawDB}>
          {langStore.monitorInspectIndexedDB}
        </Button>
        <Button variant="outlined" size="small" onClick={handleReloadFromDB}>
          {langStore.monitorReloadFromDB}
        </Button>
        <Button variant="outlined" size="small" color="warning" onClick={handleClearStack}>
          {langStore.monitorClearStack}
        </Button>
      </Stack>
    </Box>
  );
});
